import { BulkAddCartService } from "../../models/add_cart/bulk_add_cart_service";
import { BulkAddCartAnalytics, DisplayTypeForBulkAddCart } from '../analytics/bulk_add_cart_analytics';

/**
 * 複数商品を一括でカート追加できる機能を担当するAction。
 * 
 * 本Actionが動作するための条件は以下のとおり。
 * ・一括登録用カート追加ボタンにマーカクラスが付与されており、そのマーカクラスがBulkAddCartActionInitParameter.bulkAddCartButtonsClassに指定されていること。
 * ・一括登録用カート追加ボタンにdata-target-quantities-group-id属性が設けられており、カート追加ボタンに対応する数量エリアのまとまりを一意に特定できるIDが設定されていること。
 * ・カート追加ボタンに対応する数量エリア各々に、quantities-group-{上記のID} というマーカクラスが設定されていること。
 */
export class BulkAddCartAction {
  private static parameter: BulkAddCartActionInitParameter;
  private static analytics: BulkAddCartAnalytics;

  /**
   * 初期化を行う。
   * @param parameter パラメータ群
   */
  static async init(parameter: BulkAddCartActionInitParameter) {
    // 再初期化のためにパラメータを保持しておく。
    this.parameter = parameter;

    // parameter.displayTypeは、AdobeAnalyticsにデータを送る必要がある時のみinit()の呼び出し元で値が指定されるため、
    // undefinedの場合はBulkAddCartAnalyticsの初期化は必要ない。
    // BulkAddCartActionを使用する各画面でAdobeAnalytics対応が完了すれば、
    // すべてのinit()呼び出しでdisplayTypeが指定されるようになるため、いずれはこの条件式は不要になる。
    const needsAdobeAnalytics = parameter.displayType !== undefined;

    // !this.analyticsの条件は、reInit()呼び出しの際にBulkAddCartAnalyticsを再び初期化しないようにするためのもの。
    if (needsAdobeAnalytics && !this.analytics) {
      this.analytics = new BulkAddCartAnalytics(parameter.displayType);
    }

    // 一括登録用カート追加ボタンに、クリックイベントハンドラを登録する。
    const bulkAddCartButtons = document.querySelectorAll(`.${parameter.bulkAddCartButtonsClass}`);
    bulkAddCartButtons.forEach((bulkAddCartButton) => {
      bulkAddCartButton.addEventListener('click', BulkAddCartService.handleAddCartButtonClick.bind(BulkAddCartService));
    });
  }

  /**
   * 一括登録カート追加ボタンとそれに対応する数量エリア群が動的に描き変わった際に、再初期化を行う。
   * 本メソッドはItemRefineSearchContainer、VariationContainerから呼び出される。
   * 
   * キーワード検索結果画面などで絞り込みが行われた場合、画面の再描画を伴わずに各エリアが描き変わる。
   * その場合は初期表示での初期化とは別に、再度 初期化する必要がある。
   */
  static reInit() {
    // initが呼ばれずにいきなりreInitが呼び出された場合は何もしない。
    if (!this.parameter) return;

    this.init(this.parameter);
  }
}

/**
 * 初期化用のパラメータ
 */
interface BulkAddCartActionInitParameter {
  // 一括登録用のカート追加ボタンたちに付けたマーカクラス
  bulkAddCartButtonsClass: string;

  // 特定画面の一括登録用カート追加ボタンでは、AdobeAnalyticsにデータを送る必要があるためその判別をするための引数。
  displayType?: DisplayTypeForBulkAddCart;
}
